import { ColumnDef, ColumnHelper } from '@tanstack/react-table'
import { Preferences } from 'models/preferences'
import { DataTableColumnSort } from '../../../../DataTable/DataTableSorting/DataTableSortingStore'
import {
  AccountOwnerCell,
  CampaignsCell,
  ContactTagCellV2,
  DistributorsCell,
  GPOsCell,
  SalesStageCell,
  TaglistCell,
} from '../../../../FbUI/StagePill'
import { CHAIN_CRM_REPORT_COLUMNS } from './ChainCRMReportColumnsInfo'
import { createTableColumnHelper } from '../../commonColumnHelpers'
import { formatInteger } from '../../../../../utils/formatting'
import { ListWithChainCRMReport } from 'models/chain_reports'

export function getCommonChainCRMReportColumns(
  columnHelper: ColumnHelper<ListWithChainCRMReport>,
  sorting?: DataTableColumnSort[],
  preferences?: Preferences
) {
  const tooltips: Record<string, unknown> = preferences?.tooltips || {}
  const commonColumnHelper = createTableColumnHelper({
    columns: CHAIN_CRM_REPORT_COLUMNS,
    tooltips,
  })

  const cols: ColumnDef<ListWithChainCRMReport, any>[] = [
    // Campaigns from chain_crm_report
    columnHelper.accessor('chain_crm_report.campaigns', {
      ...commonColumnHelper.retrieveColumn('campaigns'),
      cell: (info) => {
        return <CampaignsCell campaigns={info.getValue()} />
      },
      size: 260,
    }),

    // Sales stages from chain_crm_report
    columnHelper.accessor('chain_crm_report.sales_stages', {
      ...commonColumnHelper.retrieveColumn('sales_stages'),
      size: 260,
      cell: (info) => {
        const salesStages = info.row.original.chain_crm_report?.sales_stages

        return (
          <SalesStageCell
            salesStagesIds={salesStages?.map((stage) => stage.id!) || []}
            sortAsc={
              sorting?.length && sorting[0].id === 'sales_stages'
                ? !sorting[0].desc
                : undefined
            }
          />
        )
      },
    }),

    // Account owners from chain_crm_report
    columnHelper.accessor('chain_crm_report.account_owners', {
      ...commonColumnHelper.retrieveColumn('account_owners'),
      size: 260,
      cell: (info) => {
        const accountOwners = info.row.original.chain_crm_report?.account_owners
        const accountOwnerIds =
          accountOwners
            ?.filter((owner) => owner.id)
            .map((owner) => owner.id!) || []

        return (
          <AccountOwnerCell
            accountOwnerIds={accountOwnerIds}
            sortAsc={
              sorting?.length && sorting[0].id === 'account_owners'
                ? !sorting[0].desc
                : undefined
            }
          />
        )
      },
    }),

    // Chain CRM Report taglist
    columnHelper.accessor('chain_crm_report.taglist', {
      ...commonColumnHelper.retrieveColumn('taglist'),
      size: 260,
      cell: (info) => {
        return (
          <TaglistCell
            taglistIds={
              info.row.original.chain_crm_report?.taglist?.map(
                (tag) => tag.id!
              ) ?? []
            }
          />
        )
      },
    }),

    // Contact tags from chain_crm_report
    columnHelper.accessor('chain_crm_report.contact_tags', {
      ...commonColumnHelper.retrieveColumn('contact_tags'),
      size: 260,
      cell: (info) => {
        const contactTags = info.row.original.chain_crm_report?.contact_tags
        return (
          <ContactTagCellV2
            contact_tags={contactTags?.map((tag) => tag.id!) ?? []}
          />
        )
      },
    }),

    // Contact count from chain_crm_report
    columnHelper.accessor('chain_crm_report.contact_count', {
      ...commonColumnHelper.retrieveColumn('contact_count', {
        meta: {
          rightAlign: true,
        },
      }),
      size: 110,
      cell: (info) => {
        const value = info.getValue()
        return value ? formatInteger(value) : '-'
      },
    }),

    // Note count from chain_crm_report
    columnHelper.accessor('chain_crm_report.note_count', {
      ...commonColumnHelper.retrieveColumn('note_count', {
        meta: {
          rightAlign: true,
        },
      }),
      size: 110,
      cell: (info) => {
        const value = info.getValue()
        return value ? formatInteger(value) : '-'
      },
    }),

    // Deal count from chain_crm_report
    columnHelper.accessor('chain_crm_report.deal_count', {
      ...commonColumnHelper.retrieveColumn('deal_count', {
        meta: {
          rightAlign: true,
        },
      }),
      size: 110,
      cell: (info) => {
        const value = info.getValue()
        return value ? formatInteger(value) : '-'
      },
    }),

    // Doors count from chain_crm_report
    columnHelper.accessor('chain_crm_report.doors_cnt', {
      ...commonColumnHelper.retrieveColumn('doors_cnt', {
        meta: {
          rightAlign: true,
        },
      }),
      size: 110,
      cell: (info) => {
        const value = info.getValue()
        return value ? formatInteger(value) : '-'
      },
    }),

    // Distributors from chain_crm_report
    columnHelper.accessor('chain_crm_report.distributors', {
      ...commonColumnHelper.retrieveColumn('distributors'),
      size: 260,
      cell: (info) => {
        return (
          <DistributorsCell
            distributorsIds={
              info.row.original.chain_crm_report?.distributors?.map(
                (distributor) => distributor.id!
              ) || []
            }
          />
        )
      },
    }),

    // GPOs from chain_crm_report
    columnHelper.accessor('chain_crm_report.gpos', {
      ...commonColumnHelper.retrieveColumn('gpos'),
      size: 260,
      cell: (info) => {
        return <GPOsCell gpos={info.getValue()} />
      },
    }),
  ]

  return cols
}
